import { Heading } from '@app/app/_components/Heading/Heading';
import { Message } from '@app/app/_components/Message/Message';
import { BeneficiaryData } from '@app/domain/model/policy/BeneficiaryData';
import { BeneficiaryFormState } from '@app/domain/model/policy/BeneficiaryFormState';
import {
    calculateAvailableShare,
    getBeneficiarySharePercent,
} from '@app/app/self-service/(wizard)/change-beneficiary-death/utils/calculateAvailableShare';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { AddBeneficiaryButton } from './AddBeneficiaryButton';
import { BeneficiaryCard } from './BeneficiaryCard';
import { BeneficiaryShareEditor } from './BeneficiaryShareEditor';

interface Props {
    existingBeneficiaries: BeneficiaryData[];
    newBeneficiaries: BeneficiaryData[];
    isCombinedShareFull: boolean;
    canAddMoreBeneficiaries: boolean;
    handleAddBeneficiary: () => void;
    removeBeneficiary: (beneficiaryId: string) => void;
    handleEditBeneficiary: (beneficiaryId: string) => void;
    beneficiaryFormState: BeneficiaryFormState;
}

export function BeneficiaryListView({
    existingBeneficiaries,
    newBeneficiaries,
    isCombinedShareFull,
    canAddMoreBeneficiaries,
    handleAddBeneficiary,
    removeBeneficiary,
    handleEditBeneficiary,
    beneficiaryFormState,
}: Props) {
    const t = useTranslations();

    const [isShareEditorOpen, setIsShareEditorOpen] = useState(false);
    const [editingBeneficiary, setEditingBeneficiary] = useState<Nullable<BeneficiaryData>>(null);

    const hasAnyBeneficiaries = existingBeneficiaries.length > 0 || newBeneficiaries.length > 0;

    const { onShareChange: onShareChangeHandler, getBeneficiaryPercentage } = beneficiaryFormState;

    const openShareEditor = useCallback(
        (beneficiaryId: string) => {
            const foundBeneficiary = existingBeneficiaries.find(beneficiary => beneficiary.id === beneficiaryId);
            if (foundBeneficiary) {
                setEditingBeneficiary(foundBeneficiary);
                setIsShareEditorOpen(true);
            }
        },
        [existingBeneficiaries],
    );

    const closeShareEditor = useCallback(() => {
        setIsShareEditorOpen(false);
        setEditingBeneficiary(null);
    }, []);

    const allBeneficiaries = useMemo(
        () => [...existingBeneficiaries, ...newBeneficiaries],
        [existingBeneficiaries, newBeneficiaries],
    );

    const maxPermittedPercentage = useMemo(() => {
        return calculateAvailableShare({
            allBeneficiaries,
            editingBeneficiaryId: editingBeneficiary?.id ?? null,
        });
    }, [allBeneficiaries, editingBeneficiary?.id]);

    const currentBeneficiaryShare = useMemo(() => getBeneficiarySharePercent(editingBeneficiary), [editingBeneficiary]);

    const handleShareSave = useCallback(
        (beneficiaryId: string, percentage: string) => {
            const currentPercentage = getBeneficiaryPercentage(beneficiaryId);
            if (currentPercentage !== percentage) {
                onShareChangeHandler(beneficiaryId, percentage);
            }
        },
        [getBeneficiaryPercentage, onShareChangeHandler],
    );

    const currentPercentage = editingBeneficiary ? getBeneficiaryPercentage(editingBeneficiary.id) : '';

    return (
        <>
            {!hasAnyBeneficiaries && <Message variant="info">{t('lifeInsurance.noBeneficiariesSet')}</Message>}

            <div className="flex flex-col gap-4">
                {existingBeneficiaries.length > 0 && (
                    <>
                        <Heading level="h4" className="text-neutral-600">
                            {t('lifeInsurance.existingBeneficiaries')}
                        </Heading>
                        {existingBeneficiaries.map((beneficiary: BeneficiaryData) => (
                            <BeneficiaryCard
                                key={`existing-${beneficiary.id}`}
                                beneficiary={beneficiary}
                                isExisting={true}
                                onEdit={openShareEditor}
                            />
                        ))}
                    </>
                )}

                {newBeneficiaries.length > 0 && (
                    <>
                        {existingBeneficiaries.length > 0 && (
                            <Heading level="h4" className="mt-6 text-neutral-600">
                                {t('lifeInsurance.newBeneficiaries')}
                            </Heading>
                        )}
                        {newBeneficiaries.map((beneficiary: BeneficiaryData) => (
                            <BeneficiaryCard
                                key={`new-${beneficiary.id}`}
                                beneficiary={beneficiary}
                                isExisting={false}
                                onEdit={handleEditBeneficiary}
                                onDelete={removeBeneficiary}
                            />
                        ))}
                    </>
                )}

                {isCombinedShareFull && <Message variant="info">{t('lifeInsurance.totalShareReached')}</Message>}

                {canAddMoreBeneficiaries && <AddBeneficiaryButton onAddBeneficiary={handleAddBeneficiary} />}
            </div>

            <BeneficiaryShareEditor
                isOpen={isShareEditorOpen}
                editingBeneficiary={editingBeneficiary}
                maxPermittedPercentage={maxPermittedPercentage}
                currentPercentage={currentPercentage}
                currentBeneficiaryShare={currentBeneficiaryShare}
                onClose={closeShareEditor}
                onSave={handleShareSave}
            />
        </>
    );
}
